/**
 * Dimark product hero — slide show/hide, dot navigation, swipe / drag between slides.
 */
(function () {
  const SWIPE_THRESHOLD = 44;
  const DRAG_AXIS_CANCEL = 12;

  function isThemeHiddenSlide(item) {
    return item.matches('.media-viewer__item--variant:not(:first-child)');
  }

  function getSlideItems(viewer) {
    return Array.from(viewer.querySelectorAll('.media-viewer__item')).filter((item) => {
      return !isThemeHiddenSlide(item);
    });
  }

  function getDotIndexForMediaId(dots, mediaId) {
    return dots.findIndex((dot) => String(dot.dataset.mediaId) === String(mediaId));
  }

  function setActiveDot(dots, index) {
    if (!dots.length || index < 0) return;
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function lockViewerScroll(viewer) {
    viewer.classList.remove('flex');
    viewer.scrollLeft = 0;
    viewer.style.overflow = 'hidden';
    viewer.style.scrollSnapType = 'none';
  }

  function setActiveSlide(viewer, items, index) {
    const activeItem = items[index];
    viewer.querySelectorAll('.media-viewer__item').forEach((item) => {
      if (isThemeHiddenSlide(item)) return;
      const isActive = item === activeItem;
      item.classList.toggle('dimark-hero-slide--active', isActive);
      item.toggleAttribute('hidden', !isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
    lockViewerScroll(viewer);
  }

  function getInitialIndex(viewer, items) {
    const current = viewer.querySelector('.media-viewer__item.is-current-variant');
    if (current && !isThemeHiddenSlide(current)) {
      const idx = items.indexOf(current);
      if (idx >= 0) return idx;
    }
    return 0;
  }

  function goToSlide(viewer, items, dots, index) {
    if (!items.length) return 0;
    const targetIndex = Math.max(0, Math.min(index, items.length - 1));
    setActiveSlide(viewer, items, targetIndex);

    if (dots.length) {
      const dotIndex = getDotIndexForMediaId(dots, items[targetIndex].dataset.mediaId);
      setActiveDot(dots, dotIndex >= 0 ? dotIndex : targetIndex);
    }

    viewer.classList.add('dimark-hero--ready');
    viewer.classList.toggle('dimark-hero--swipeable', items.length > 1);
    return targetIndex;
  }

  function isInteractiveTarget(target) {
    return !!target.closest('button, a, input, textarea, select, label, video, model-viewer, product-model, .media-ctrl__btn');
  }

  function initSwipe(viewer, getState, onStep) {
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let axisCancelled = false;

    function resetPointer() {
      pointerId = null;
      axisCancelled = false;
      viewer.classList.remove('dimark-hero--grabbing');
    }

    viewer.addEventListener('pointerdown', (event) => {
      const { items } = getState();
      if (items.length < 2) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (isInteractiveTarget(event.target)) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      axisCancelled = false;
      viewer.classList.add('dimark-hero--grabbing');

      try {
        viewer.setPointerCapture(event.pointerId);
      } catch (err) {
        /* ignore */
      }
    });

    viewer.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!axisCancelled && Math.abs(dy) > DRAG_AXIS_CANCEL && Math.abs(dy) > Math.abs(dx)) {
        axisCancelled = true;
        viewer.classList.remove('dimark-hero--grabbing');
      }
    }, { passive: true });

    viewer.addEventListener('pointerup', (event) => {
      if (event.pointerId !== pointerId) return;

      const { items } = getState();
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      resetPointer();

      if (items.length < 2 || axisCancelled) return;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dx) <= Math.abs(dy)) return;

      const rtl = document.documentElement.dir === 'rtl'
        || getComputedStyle(document.documentElement).direction === 'rtl';

      if (dx < 0) {
        onStep(rtl ? -1 : 1);
      } else {
        onStep(rtl ? 1 : -1);
      }
    });

    viewer.addEventListener('pointercancel', (event) => {
      if (event.pointerId !== pointerId) return;
      resetPointer();
    });
  }

  function initDimarkHeroGallery() {
    const stage = document.querySelector('.dimark-hero__stage');
    if (!stage) return;

    const gallery = stage.querySelector('media-gallery');
    if (!gallery) return;

    const viewer = gallery.querySelector('.media-viewer');
    if (!viewer) return;

    lockViewerScroll(viewer);
    viewer.addEventListener('scroll', () => {
      if (viewer.scrollLeft !== 0) viewer.scrollLeft = 0;
    }, { passive: true });

    const dotsNav = stage.querySelector('.dimark-hero__dots');
    const dots = dotsNav ? Array.from(dotsNav.querySelectorAll('.dimark-hero__dot')) : [];

    let currentIndex = 0;

    function refreshSlides(preferredMediaId) {
      const items = getSlideItems(viewer);
      if (!items.length) return;

      let nextIndex = currentIndex;
      if (preferredMediaId) {
        const match = items.findIndex((item) => String(item.dataset.mediaId) === String(preferredMediaId));
        if (match >= 0) nextIndex = match;
      } else {
        nextIndex = getInitialIndex(viewer, items);
      }

      currentIndex = goToSlide(viewer, items, dots, nextIndex);
    }

    initSwipe(
      viewer,
      () => ({ items: getSlideItems(viewer), currentIndex }),
      (delta) => {
        const items = getSlideItems(viewer);
        currentIndex = goToSlide(viewer, items, dots, currentIndex + delta);
      }
    );

    stage.addEventListener('keydown', (event) => {
      const items = getSlideItems(viewer);
      if (items.length < 2) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const rtl = document.documentElement.dir === 'rtl'
        || getComputedStyle(document.documentElement).direction === 'rtl';
      let delta = 0;

      if (event.key === 'ArrowLeft') {
        delta = rtl ? 1 : -1;
      } else if (event.key === 'ArrowRight') {
        delta = rtl ? -1 : 1;
      }

      if (delta !== 0) {
        event.preventDefault();
        currentIndex = goToSlide(viewer, items, dots, currentIndex + delta);
      }
    });

    if (dotsNav) {
      dotsNav.addEventListener('click', (event) => {
        const dot = event.target.closest('.dimark-hero__dot');
        if (!dot) return;

        const items = getSlideItems(viewer);
        const target = viewer.querySelector(
          `.media-viewer__item[data-media-id="${dot.dataset.mediaId}"]`
        );
        if (!target || isThemeHiddenSlide(target)) return;

        const itemIndex = items.indexOf(target);
        if (itemIndex >= 0) {
          currentIndex = goToSlide(viewer, items, dots, itemIndex);
        }
      });
    }

    const productRoot = gallery.closest('.js-product');
    if (productRoot) {
      productRoot.addEventListener('on:variant:change', (event) => {
        const featuredId = event.detail?.variant?.featured_media?.id;
        window.setTimeout(() => {
          refreshSlides(featuredId || null);
        }, 120);
      });
    }

    window.addEventListener('resize', () => {
      refreshSlides();
    });

    refreshSlides();
    if (typeof window.__dimarkHeroLayoutUpdate === 'function') {
      window.__dimarkHeroLayoutUpdate();
    }
  }

  /** Keeps the photo ↔ buy-card gap centred on the layout (mockup black strip). */
  function initDimarkHeroLayout() {
    const layout = document.querySelector('.dimark-layout');
    if (!layout) return;

    const section = layout.closest('.shopify-section');
    const stage = layout.querySelector('.dimark-hero__stage');
    const desktop = window.matchMedia('(min-width: 901px)');

    const update = () => {
      if (!section) return;
      if (!desktop.matches || !stage) {
        section.style.removeProperty('--dimark-hero-rendered-width');
        return;
      }
      const activeImg = stage.querySelector(
        '.media-viewer__item.dimark-hero-slide--active img, .media-viewer__item.is-current-variant img, .media-viewer__item img'
      );
      const width = Math.round(
        activeImg ? activeImg.getBoundingClientRect().width : stage.getBoundingClientRect().width
      );
      if (width > 0) {
        section.style.setProperty('--dimark-hero-rendered-width', `${width}px`);
      }
    };

    if (stage && 'ResizeObserver' in window) {
      const observer = new ResizeObserver(update);
      observer.observe(stage);
    }

    layout.querySelectorAll('.dimark-hero__media img').forEach((img) => {
      if (img.complete) update();
      else img.addEventListener('load', update, { once: true });
    });

    window.addEventListener('resize', update, { passive: true });
    if (typeof desktop.addEventListener === 'function') {
      desktop.addEventListener('change', update);
    }
    update();
    window.__dimarkHeroLayoutUpdate = update;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initDimarkHeroGallery();
      initDimarkHeroLayout();
    });
  } else {
    initDimarkHeroGallery();
    initDimarkHeroLayout();
  }
})();
